import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, Send, X, PenLine, Check, Search, Lightbulb } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Avatar, Help, fmtDate, rise } from './shared';

const CHIPS = [
  'Wie wirke ich in Gesprächen mit Verkäufern?',
  'War meine letzte Analyse verständlich?',
  'Wie effektiv ist meine Kommunikation im Projekt?',
  'Was würdet ihr an meiner Stelle als Nächstes priorisieren?',
];

export default function RequestsTab({ members, meId, onAnswer, refreshCounts }) {
  const [data, setData] = useState(null);
  const [rtype, setRtype] = useState('observation');
  const [selected, setSelected] = useState([]);
  const [topic, setTopic] = useState('');
  const [saving, setSaving] = useState(false);
  const others = members.filter((m) => m.id !== meId);

  const load = useCallback(() => {
    api.teamDevRequests().then((res) => setData(res.data)).catch(() => toast.error('Anfragen konnten nicht geladen werden'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelected((s) => {
    if (s.includes(id)) return s.filter((x) => x !== id);
    if (s.length >= 2) { toast('Maximal 2 Personen — gezielt fragen bringt bessere Antworten.'); return s; }
    return [...s, id];
  });

  const create = async () => {
    if (!topic.trim()) return toast('Magst du kurz beschreiben, wozu du Feedback möchtest?');
    if (!selected.length) return toast('Wähle 1–2 Kolleg:innen aus.');
    setSaving(true);
    try {
      await api.teamDevCreateRequest({ to_user_ids: selected, topic: topic.trim(), request_type: rtype });
      toast.success('Deine Anfrage ist raus — selbst zu fragen ist der beste Move. 🌱', { duration: 5000 });
      setTopic(''); setSelected([]); load(); refreshCounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Anfrage hat nicht geklappt');
    } finally { setSaving(false); }
  };

  const dismiss = async (rid) => {
    try {
      await api.teamDevDismissRequest(rid);
      toast('Kein Problem — vielleicht ein anderes Mal.');
      load(); refreshCounts();
    } catch { toast.error('Das hat nicht geklappt'); }
  };

  const statusChip = (s) => {
    const map = { open: ['offen', 'border-amber-500/40 bg-amber-500/10 text-amber-400'],
      done: ['beantwortet', 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400'],
      dismissed: ['übersprungen', 'border-border bg-transparent text-muted-foreground'] };
    const [l, c] = map[s] || map.open;
    return <span className={`text-[9px] font-mono uppercase border rounded px-1.5 py-0.5 ${c}`}>{l}</span>;
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="td-requests">
      <div className="td-rise bg-card border border-border rounded-lg p-6 space-y-4">
        <div>
          <p className="text-sm font-medium text-foreground">Feedback anfordern</p>
          <p className="text-xs text-muted-foreground mt-1">Selbst zu fragen ist ein Zeichen von Stärke, nicht von Schwäche.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-2.5">
          <button onClick={() => setRtype('observation')} data-testid="td-reqtype-observation"
            className={`td-card text-left rounded-lg border p-4 transition-colors ${rtype === 'observation' ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-border bg-secondary/20 hover:bg-secondary/40'}`}>
            <p className="text-sm text-foreground flex items-center gap-2"><Search className="w-4 h-4 text-sky-400" /> Beobachtung</p>
            <p className="text-[11px] text-muted-foreground mt-1">Wie wirke ich? Wie kommt etwas an?</p>
          </button>
          <button onClick={() => setRtype('feedforward')} data-testid="td-reqtype-feedforward"
            className={`td-card text-left rounded-lg border p-4 transition-colors ${rtype === 'feedforward' ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-border bg-secondary/20 hover:bg-secondary/40'}`}>
            <p className="text-sm text-foreground flex items-center gap-2"><Lightbulb className="w-4 h-4 text-violet-400" /> Ideen</p>
            <p className="text-[11px] text-muted-foreground mt-1">Was würdet ihr an meiner Stelle tun?</p>
          </button>
        </div>
        <div>
          <p className="text-xs font-medium text-foreground mb-1.5">Wozu genau möchtest du Feedback?</p>
          <Input value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="td-request-topic"
            placeholder="Deine konkrete Frage …" className="text-sm bg-background rounded-md" />
          <div className="flex flex-wrap gap-1.5 mt-2">
            {CHIPS.map((c, i) => (
              <button key={i} onClick={() => setTopic(c)} data-testid={`td-request-chip-${i}`}
                className="text-[10px] rounded-full border border-border px-2.5 py-1 text-muted-foreground hover:text-foreground hover:border-emerald-500/40 transition-colors">
                {c}
              </button>
            ))}
          </div>
          <Help>Je konkreter die Frage, desto besser die Antwort. ‚Wie war mein Teil im Mentoren-Call gestern?‘ schlägt ‚Feedback zu mir allgemein‘.</Help>
        </div>
        <div>
          <p className="text-xs font-medium text-foreground mb-1.5">Wen fragst du? <span className="text-muted-foreground font-normal">(1–2 Personen · Frist: 1 Woche)</span></p>
          <div className="flex flex-wrap gap-2">
            {others.map((m) => (
              <button key={m.id} onClick={() => toggle(m.id)} data-testid={`td-request-target-${m.id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-colors ${
                  selected.includes(m.id) ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300' : 'border-border bg-secondary/30 text-foreground hover:bg-secondary/60'}`}>
                {selected.includes(m.id) && <Check className="w-3 h-3" />}
                <Avatar name={m.name} className="w-4 h-4 text-[9px]" />
                {m.name}
              </button>
            ))}
          </div>
        </div>
        <Button onClick={create} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6" data-testid="td-request-submit">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
          Anfrage senden
        </Button>
      </div>

      {data?.for_me?.length > 0 && (
        <div className="space-y-2.5">
          <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Jemand wünscht sich dein Feedback</p>
          {data.for_me.map((r, i) => (
            <div key={r.id} style={rise(i)} className="td-rise td-card bg-card border border-emerald-500/30 rounded-lg p-4 flex items-center gap-3 flex-wrap" data-testid={`td-request-forme-${i}`}>
              <Avatar name={r.from_name} />
              <div className="flex-1 min-w-[200px]">
                <p className="text-sm text-foreground"><span className="font-medium">{r.from_name}</span> möchte wissen:</p>
                <p className="text-sm text-foreground/90 mt-0.5">„{r.topic}“</p>
                <p className="text-[10px] text-muted-foreground mt-1">
                  {r.request_type === 'feedforward' ? '💡 Ideen' : '🔍 Beobachtung'} · {fmtDate(r.created_at)}
                  {typeof r.days_left === 'number' && <> · {r.days_left > 0 ? `noch ${r.days_left} Tag${r.days_left === 1 ? '' : 'e'}` : r.days_left === 0 ? 'heute fällig' : 'Frist vorbei — antworten geht trotzdem'}</>}
                </p>
              </div>
              <Button size="sm" onClick={() => onAnswer(r)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs rounded-full" data-testid={`td-answer-request-${i}`}>
                <PenLine className="w-3 h-3 mr-1" /> Feedback schreiben
              </Button>
              <button onClick={() => dismiss(r.id)} title="Gerade nicht möglich" data-testid={`td-dismiss-request-${i}`}
                className="text-muted-foreground/50 hover:text-foreground p-1"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}

      {data?.mine?.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Deine Anfragen</p>
          {data.mine.map((r, i) => (
            <div key={r.id} className="bg-card border border-border rounded-lg px-4 py-2.5 flex items-center gap-2 flex-wrap" data-testid={`td-request-mine-${i}`}>
              <p className="text-xs text-foreground flex-1 min-w-[180px]">an <span className="font-medium">{r.to_name}</span>: „{r.topic}“</p>
              {statusChip(r.status)}
              <span className="text-[10px] text-muted-foreground font-mono">{fmtDate(r.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
